// Parent dashboard home. Greeting + quick routes into the two live R5 screens:
// the Children panel and the progress dashboard.

import { useT } from '@fajrarabi/i18n/react';
import { Button, Card, CardBody } from '@fajrarabi/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { useAuth } from '~/lib/auth';
import { getSubscription } from '~/lib/billing';
import { asLocale } from '~/lib/locale';

// The API is the authority on entitlement (lib/billing.ts
// `isEntitlingSubscription`). This was a 4th hand-rolled copy of the status
// list — and the one that still listed `trialing` after trials were removed.
// It only drives a nudge banner, so an approximate answer is acceptable; a
// DIVERGENT one is not. Keep it a strict subset of what the API entitles:
// `past_due` is deliberately absent because the client cannot see whether an
// invoice was ever paid, and showing a subscriber a "subscribe" nudge is a
// smaller sin than hiding it from someone who needs to pay.
const NUDGE_SUPPRESSING = new Set(['active', 'past_due']);

export function ParentHome() {
  const t = useT();
  const { user } = useAuth();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const name = user?.displayName ?? user?.email ?? '';

  // Show the subscribe nudge only when there's no entitling subscription.
  const subQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: ({ signal }) => getSubscription(signal),
    staleTime: 60_000,
  });
  const sub = subQuery.data?.subscription ?? null;
  const showNudge = subQuery.isSuccess && (!sub || !NUDGE_SUPPRESSING.has(sub.status));

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.parent.greeting', { name })}</h1>
      <p className="mt-1 text-ink-700">{t('app.parent.dashboard_subtitle')}</p>

      {showNudge && (
        <div className="mt-6 overflow-hidden rounded-2xl bg-burgundy-500 p-6 text-cream-50 shadow-[0_18px_40px_-20px_rgba(139,44,61,0.5)]">
          <span className="inline-block rounded-full bg-cream-50/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            {t('app.paywall.trial_badge')}
          </span>
          <p className="mt-3 text-xl font-bold">{t('app.paywall.nudge_title')}</p>
          <p className="mt-1 text-sm text-cream-100/90">{t('app.paywall.nudge_body')}</p>
          <Link to="/$locale/billing" params={{ locale }} className="mt-4 inline-block">
            <Button variant="secondary">{t('app.paywall.nudge_cta')}</Button>
          </Link>
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <Card>
          <CardBody>
            <p className="text-lg font-bold text-ink-900">{t('app.parent.kids_title')}</p>
            <p className="mt-1 text-sm text-ink-700">{t('app.parent.kids_subtitle')}</p>
            <Link to="/$locale/kids" params={{ locale }} className="mt-5 inline-block">
              <Button>{t('app.parent.add_kid')}</Button>
            </Link>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-lg font-bold text-ink-900">{t('app.nav.progress')}</p>
            <p className="mt-1 text-sm text-ink-700">{t('app.progress.pick_kid')}</p>
            <Link to="/$locale/progress" params={{ locale }} className="mt-5 inline-block">
              <Button variant="secondary">{t('app.progress.view')}</Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
